import {
    DeleteSubscriptionPayload,
    GeographicInformationsPayload,
    IMySavedItemsListPayload,
    CreateSubscriptionPayload,
    BasePaymentMethodPayload,
    AddPaymentMethodPayload,
    PersonalizationSettingsPayload,
    SetDefaultStorePayload,
    UpdatePasswordPayload,
    UpdatePersonalDetailsPayload,
    UpdatePersonalInformationPayload,
    AddressPayload,
    AddressGroupPayload,
} from '@interfaces/myProfileInterfaces';

import * as actions from '@redux-actions/';

import {
    mapFavouritesStoresList,
    mapGeographicInformations,
    mapPersonalDetails,
    mapPersonalInformation,
    mapSavedItemsList,
} from '@utils/pages-utils/MyProfileUtils';

import { getMockedFile } from '@utils/utils';

import URLS from '@utils/urlList';

import axios, { AxiosRequestConfig } from 'axios';

import DOMPurify from 'dompurify';

//TODO: Change with B.E. service

//Utility function for sanitizing input using DOMPurify
const htmlEncode = (input: string): string => {
    return DOMPurify.sanitize(input, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
};

//PERSONAL DETAILS

export const getPersonalDetails = (isMockRequest: boolean, storeId: string) => {
    if (isMockRequest) {
        const personalDetails = '/data/mocks/my_profile_get_personal_details_mock.json';

        return dispatch => {
            dispatch(actions.getPersonalDetailsPending());
            getMockedFile(personalDetails)
                .then(result => {
                    const mappedResult = mapPersonalInformation(result?.data);
                    dispatch(actions.getPersonalDetailsSuccess(mappedResult));
                })
                .catch(() => {
                    dispatch(actions.getPersonalDetailsError());
                });
        };
    } else {
        const getPersonalDetailsUrl = URLS.GET_PERSONAL_DETAILS.replace(
            '{storeId}',
            storeId
        ).replace('{noCache}', new Date().getTime().toString());

        const config: AxiosRequestConfig = {
            method: 'GET',
            url: getPersonalDetailsUrl,
            headers: { 'Content-Type': 'application/json' },
        };
        return dispatch => {
            dispatch(actions.getPersonalDetailsPending());
            axios(config)
                .then(result => {
                    const mappedResult = mapPersonalInformation(result?.data);
                    dispatch(actions.getPersonalDetailsSuccess(mappedResult));
                })
                .catch(() => {
                    dispatch(actions.getPersonalDetailsError());
                });
        };
    }
};

export const updatePersonalDetails = (payload: UpdatePersonalInformationPayload) => {
    const { isMockRequest, storeId, ...rest } = payload;

    //Sanitize specific fields before making the API request
    const sanitizedData = {
        ...rest,
        logonId: htmlEncode(rest.logonId),
        demographicField5: htmlEncode(rest.demographicField5),
        demographicField7: htmlEncode(rest.demographicField7),
    };

    if (isMockRequest) {
        const updatePersonaDetailsResponse =
            '/data/mocks/my_profile_update_personal_details_mock.json';
        return dispatch => {
            dispatch(actions.updatePersonalDetailsPending());
            getMockedFile(updatePersonaDetailsResponse)
                .then(response => {
                    response?.data?.success === 'true'
                        ? (dispatch(actions.updatePersonalDetailsSuccess()),
                            dispatch(getPersonalDetails(isMockRequest, storeId.toString())))
                        : dispatch(actions.updatePersonalDetailsError());
                })
                .catch(() => dispatch(actions.updatePersonalDetailsError()));
        };
    } else {
        const updatePersonalDetailsUrl = URLS.UPDATE_PERSONAL_DETAILS.replace(
            '{storeId}',
            storeId.toString()
        );
        const config: AxiosRequestConfig = {
            method: 'PUT',
            url: updatePersonalDetailsUrl,
            headers: { 'Content-Type': 'application/json' },
            data: sanitizedData,
        };
        return dispatch => {
            dispatch(actions.updatePersonalDetailsPending());
            axios(config)
                .then(
                    () => (
                        dispatch(actions.updatePersonalDetailsSuccess()),
                        dispatch(getPersonalDetails(isMockRequest, storeId.toString()))
                    )
                )
                .catch(() => dispatch(actions.updatePersonalDetailsError()));
        };
    }
};

export const getPersonalDetailsCityAndState = (payload: GeographicInformationsPayload) => {
    const { isMockRequest, zipCode, addressType } = payload;
    if (isMockRequest) {
        const getGeographicInformations =
            '/data/mocks/my_profile_personal_details_get_geographic_informations_mock.json';
        return dispatch => {
            dispatch(actions.getGeographicInformationsPending());
            getMockedFile(getGeographicInformations)
                .then(result => {
                    const mappedResult = mapGeographicInformations(
                        result?.data?.results?.[0]?.address_components,
                        addressType
                    );
                    dispatch(actions.getGeographicInformationsSuccess(mappedResult));
                })
                .catch(() => dispatch(actions.getDashboardInformationsError()));
        };
    } else {
        return dispatch => {
            dispatch(actions.getGeographicInformationsPending());
            let geocoder: google.maps.Geocoder = new google.maps.Geocoder();
            geocoder
                .geocode({ address: zipCode })
                .then(result => {
                    const { results } = result;
                    const mappedResult = mapGeographicInformations(
                        results?.[0]?.address_components,
                        addressType
                    );
                    dispatch(actions.getGeographicInformationsSuccess(mappedResult));
                })
                .catch(() => dispatch(actions.getDashboardInformationsError()));
        };
    }
};

//SETTINGS

export const updateSettingsPassword = (payload: UpdatePasswordPayload) => {
    const { isMockRequest, ...rest } = payload;

    if (isMockRequest) {
        const updateSettingsPasswordResponse =
            '/data/mocks/my_profile_settings_update_password_mock.json';
        return dispatch => {
            dispatch(actions.updateSettingsPasswordPending());
            getMockedFile(updateSettingsPasswordResponse)
                .then(result => {
                    result?.data?.success === 'true'
                        ? dispatch(actions.updateSettingsPasswordSuccess())
                        : dispatch(actions.updateSettingsPasswordError());
                })
                .catch(() => dispatch(actions.updateSettingsPasswordError()));
        };
    } else {
        const updateSettingsPasswordUrl = URLS.UPDATE_SETTING_PASSWORD.replace(
            '{storeId}',
            rest.storeId.toString()
        );

        const config: AxiosRequestConfig = {
            method: 'POST',
            url: updateSettingsPasswordUrl,
            headers: { 'Content-Type': 'application/json' },
            data: rest,
        };

        return dispatch => {
            dispatch(actions.updateSettingsPasswordPending());
            axios(config)
                .then(result => {
                    result?.data?.success === 'true'
                        ? dispatch(actions.updateSettingsPasswordSuccess())
                        : dispatch(actions.updateSettingsPasswordError());
                })
                .catch(error => {
                    console.log("Scenario d'errore", error.response),
                        (error.response.data.errors.find(error => error?.errorCode === '2050') &&
                            dispatch(
                                actions.updateSettingsPasswordCustomError(
                                    'SETTINGS_UPDATE_PASSWORD_INVALID_CURRENT_PASSWORD'
                                )
                            ),
                            dispatch(actions.updateSettingsPasswordError()));
                });
        };
    }
};

export const getUserSettings = (isMockRequest: boolean, storeId: number) => {
    if (isMockRequest) {
        const userSettings = '/data/mocks/my_profile_get_settings_mock.json';
        return dispatch => {
            dispatch(actions.getSettingsUserSettingsPending());
            getMockedFile(userSettings)
                .then(result => {
                    dispatch(actions.getSettingsUserSettingsSuccess(result?.data));
                })
                .catch(() => {
                    dispatch(actions.getSettingsUserSettingsError());
                });
        };
    } else {
        const userSettingsUrl = URLS.GET_USER_SETTINGS.replace(
            '{storeId}',
            storeId.toString()
        ).replace('{noCache}', new Date().getTime().toString());

        const config: AxiosRequestConfig = {
            method: 'GET',
            url: userSettingsUrl,
            headers: { 'Content-Type': 'application/json' },
        };
        return dispatch => {
            dispatch(actions.getSettingsUserSettingsPending());
            axios(config)
                .then(result => {
                    dispatch(actions.getSettingsUserSettingsSuccess(result?.data));
                })
                .catch(() => {
                    dispatch(actions.getSettingsUserSettingsError());
                });
        };
    }
};

export const updatePersonalizationSettings = (payload: PersonalizationSettingsPayload) => {
    const { isMockRequest, ...rest } = payload;
    if (isMockRequest) {
        const updatePersonalizationSettingsResponse =
            '/data/mocks/my_profile_update_personalization_settings_mock.json';
        return dispatch => {
            dispatch(actions.updatePersonalizationSettingsPending());
            getMockedFile(updatePersonalizationSettingsResponse)
                .then(response => {
                    response?.data.success === 'true'
                        ? dispatch(actions.updatePersonalizationSettingsSuccess())
                        : dispatch(actions.updatePersonalizationSettingsError());
                })
                .catch(() => dispatch(actions.updatePersonalizationSettingsError()));
        };
    } else {
        const updatePersonalizationSettingsUrl = URLS.UPDATE_PERSONALIZATION_SETTINGS.replace(
            '{storeId}',
            rest.storeId.toString()
        );
        const config: AxiosRequestConfig = {
            method: 'POST',
            url: updatePersonalizationSettingsUrl,
            headers: { 'Content-Type': 'application/json' },
            data: rest,
        };

        return dispatch => {
            dispatch(actions.updatePersonalizationSettingsPending());
            axios(config)
                .then(response => {
                    response?.data.success === 'true'
                        ? dispatch(actions.updatePersonalizationSettingsSuccess())
                        : dispatch(actions.updatePersonalizationSettingsError());
                })
                .catch(() => dispatch(actions.updatePersonalizationSettingsError()));
        };
    }
};

export const createSubscription = (payload: CreateSubscriptionPayload) => {
    const { isMockRequest, ...rest } = payload;

    if (isMockRequest) {
        const createSubscriptionResponse = '/data/mocks/my_profile_create_subscription_mock.json';
        return dispatch => {
            dispatch(actions.createSubscriptionPending());
            getMockedFile(createSubscriptionResponse)
                .then(response => {
                    response?.data?.success === 'true'
                        ? (dispatch(actions.createSubscriptionSuccess()),
                            dispatch(actions.getUserSubscriptionCorrect(true)))
                        : (dispatch(actions.createSubscriptionError()),
                            dispatch(actions.getUserSubscriptionCorrect(false)));
                })
                .catch(() => (dispatch(actions.createSubscriptionError()),
                    dispatch(actions.getUserSubscriptionCorrect(false))));
        };
    } else {
        const createSubscriptionUrl = URLS.CREATE_SUBSCRIPTION.replace(
            '{storeId}',
            rest.storeId.toString()
        );
        const config: AxiosRequestConfig = {
            method: 'POST',
            url: createSubscriptionUrl,
            headers: { 'Content-Type': 'application/json' },
            data: rest,
        };
        return dispatch => {
            dispatch(actions.createSubscriptionPending());
            axios(config)
                .then(response => {
                    response?.data?.success === 'true'
                        ? (dispatch(actions.createSubscriptionSuccess()),
                            dispatch(getUserSettings(isMockRequest, rest.storeId)),
                            dispatch(actions.getUserSubscriptionCorrect(true)))
                        : (dispatch(actions.createSubscriptionError()),
                            dispatch(actions.getUserSubscriptionCorrect(false)));
                })
                .catch(() => (dispatch(actions.createSubscriptionError()),
                    dispatch(actions.getUserSubscriptionCorrect(false))));
        };
    }
};

export const deleteSubscription = (payload: DeleteSubscriptionPayload) => {
    const { storeId, ...rest } = payload;
    const deleteSubscriptionUrl = URLS.DELETE_SUBSCRIPTION.replace('{storeId}', storeId.toString());
    const config: AxiosRequestConfig = {
        method: 'POST',
        url: deleteSubscriptionUrl,
        headers: { 'Content-Type': 'application/json' },
        data: rest,
    };
    return dispatch => {
        dispatch(actions.deleteSubscriptionPending());
        axios(config)
            .then(() => dispatch(actions.deleteSubscriptionSuccess))
            .catch(() => dispatch(actions.deleteSubscriptionError()));
    };
};

//MY STORES

export const getFavouritesStores = (isMockRequest: boolean, storeId: string) => {
    if (isMockRequest) {
        const favouritesStores = '/data/mocks/my_stores_get_favourites_stores_mock.json';
        return dispatch => {
            dispatch(actions.getFavouritesStoresPending());
            getMockedFile(favouritesStores)
                .then(result => {
                    let response;
                    if (!result?.data?.dataList?.length) {
                        response = [];
                    } else {
                        response = mapFavouritesStoresList(
                            !result?.data?.dataList && result?.data
                                ? [result?.data]
                                : result?.data?.dataList
                        );
                    }

                    dispatch(actions.getFavouritesStoresSuccess(response));
                })
                .catch(() => {
                    dispatch(actions.getFavouritesStoresError());
                });
        };
    } else {
        const getFavouritesStoresUrl = URLS.GET_FAVOURITES_STORES.replace(
            '{storeId}',
            storeId
        ).replace('{noCache}', new Date().getTime().toString());

        const config: AxiosRequestConfig = {
            method: 'GET',
            url: getFavouritesStoresUrl,
            headers: { 'Content-Type': 'application/json' },
        };

        return dispatch => {
            dispatch(actions.getFavouritesStoresPending());
            axios(config)
                .then(result => {
                    let response;
                    if (result.status === 204) {
                        response = [];
                    } else {
                        response = mapFavouritesStoresList(
                            !result?.data?.dataList && result?.data
                                ? [result?.data]
                                : result?.data?.dataList
                        );
                    }

                    dispatch(actions.getFavouritesStoresSuccess(response));
                })
                .catch(() => {
                    dispatch(actions.getFavouritesStoresError());
                });
        };
    }
};

export const removeFavouriteStore = (storeId: string, physicalStoreId: string) => {
    const removeFavouriteStoreUrl = URLS.REMOVE_FAVOURITE_STORE.replace(
        '{storeId}',
        storeId
    ).replace('{storeLocId}', physicalStoreId);

    const config: AxiosRequestConfig = {
        method: 'DELETE',
        url: removeFavouriteStoreUrl,
        headers: { 'Content-Type': 'application/json' },
    };

    return dispatch => {
        dispatch(actions.removeFavouriteStorePending());
        axios(config)
            .then(() => {
                dispatch(actions.removeFavouriteStoreSuccess());
                dispatch(getFavouritesStores(false, storeId));
            })
            .catch(() => {
                dispatch(actions.removeFavouriteStoreError());
            });
    };
};

export const setDefaultStore = (storeId: string, payload: SetDefaultStorePayload) => {
    const setDefaultStoreUrl = URLS.SET_DEFAULT_STORE.replace('{storeId}', storeId);

    const config: AxiosRequestConfig = {
        method: 'POST',
        url: setDefaultStoreUrl,
        headers: { 'Content-Type': 'application/json' },
        data: payload,
    };

    return dispatch => {
        dispatch(actions.setDefaultStorePending());
        axios(config)
            .then(() => {
                dispatch(actions.setDefaultStoreSuccess());
                dispatch(getFavouritesStores(false, storeId));
            })
            .catch(() => {
                dispatch(actions.setDefaultStoreError());
            });
    };
};

//PAYMENT METHODS

export const addPaymentMethod = (
    isMockRequest: boolean,
    storeId: string,
    payload: AddPaymentMethodPayload
) => {
    const addPaymentMethodUrl = URLS.ADD_PAYMENT_METHOD.replace('{storeId}', storeId);

    const config: AxiosRequestConfig = {
        method: 'PUT',
        url: addPaymentMethodUrl,
        headers: { 'Content-Type': 'application/json' },
        data: payload,
    };

    return dispatch => {
        dispatch(actions.addPaymentMethodPending());
        axios(config)
            .then(response => {
                response.data.ccAuthReply_reasonCode == 100
                    ? (dispatch(getPaymentMethods(isMockRequest, storeId)),
                        dispatch(actions.addPaymentMethodSuccess()))
                    : dispatch(actions.addPaymentMethodError());
            })
            .catch(() => {
                dispatch(actions.addPaymentMethodError());
            });
    };
};

export const editPaymentMethod = (
    isMockRequest: boolean,
    storeId: string,
    paymentId: number,
    payload: BasePaymentMethodPayload
) => {
    const editPaymentMethodUrl = URLS.EDIT_PAYMENT_METHOD.replace('{storeId}', storeId).replace(
        '{paymentId}',
        paymentId.toString()
    );

    const config: AxiosRequestConfig = {
        method: 'POST',
        url: editPaymentMethodUrl,
        headers: { 'Content-Type': 'application/json' },
        data: payload,
    };

    return dispatch => {
        dispatch(actions.editPaymentMethodPending());
        axios(config)
            .then(response => {
                response.data.reasonCode == 100
                    ? (dispatch(getPaymentMethods(isMockRequest, storeId)),
                        dispatch(actions.editPaymentMethodSuccess()))
                    : dispatch(actions.editPaymentMethodError());
            })
            .catch(() => {
                dispatch(actions.editPaymentMethodError());
            });
    };
};

export const getPaymentMethods = (isMockRequest: boolean, storeId: string) => {
    if (isMockRequest) {
        const paymentMethodsList = '/data/mocks/payment_methods_get_payment_methods_mock.json';
        return dispatch => {
            dispatch(actions.getPaymentMethodsPending());
            getMockedFile(paymentMethodsList)
                .then(result => {
                    dispatch(actions.getPaymentMethodsSuccess(result?.data));
                })
                .catch(() => dispatch(actions.getPaymentMethodsError()));
        };
    } else {
        const getPaymentMethodsListUrl = URLS.GET_PAYMENT_METHODS.replace(
            '{storeId}',
            storeId
        ).replace('{noCache}', new Date().getTime().toString());

        const config: AxiosRequestConfig = {
            method: 'GET',
            url: getPaymentMethodsListUrl,
            headers: { 'Content-Type': 'application/json' },
        };

        return dispatch => {
            dispatch(actions.getPaymentMethodsPending());
            axios(config)
                .then(result => {
                    dispatch(actions.getPaymentMethodsSuccess(result?.data));
                })
                .catch(() => dispatch(actions.getPaymentMethodsError()));
        };
    }
};

export const setDefaultPaymentMethod = (
    isMockRequest: boolean,
    storeId: string,
    paymentId: number
) => {
    const setDefaultPaymentMethodUrl = URLS.SET_DEFAULT_PAYMENT_METHOD.replace(
        '{storeId}',
        storeId
    ).replace('{paymentId}', paymentId.toString());

    const config: AxiosRequestConfig = {
        method: 'POST',
        url: setDefaultPaymentMethodUrl,
        headers: { 'Content-Type': 'application/json' },
    };

    return dispatch => {
        dispatch(actions.setDefaultPaymentMethodPending());
        axios(config)
            .then(
                () => (
                    dispatch(actions.setDefaultPaymentMethodSuccess()),
                    dispatch(getPaymentMethods(isMockRequest, storeId))
                )
            )
            .catch(() => dispatch(actions.setDefaultPaymentMethodError()));
    };
};

export const removePaymentMethod = (
    isMockRequest: boolean,
    storeId: string,
    deletePaymentId: number
) => {
    const removePaymentMethodUrl = URLS.REMOVE_PAYMENT_METHOD.replace('{storeId}', storeId).replace(
        '{deletePaymentId}',
        deletePaymentId.toString()
    );

    const config: AxiosRequestConfig = {
        method: 'DELETE',
        url: removePaymentMethodUrl,
        headers: { 'Content-Type': 'application/json' },
    };

    return dispatch => {
        dispatch(actions.removePaymentMethodPending());
        axios(config)
            .then(
                () => dispatch(getPaymentMethods(isMockRequest, storeId)),
                dispatch(actions.removePaymentMethodSuccess())
            )
            .catch(() => dispatch(actions.removePaymentMethodError()));
    };
};

// ADDRESSES
export const getAddresses = (isMockRequest: boolean, storeId: string) => {
    if (isMockRequest) {
        const addresses = '/data/mocks/my_addresses_get_addresses.json';
        return dispatch => {
            dispatch(actions.getAddressesPending());
            getMockedFile(addresses)
                .then(result => {
                    dispatch(actions.getAddressesSuccess(result?.data));
                })
                .catch(() => dispatch(actions.getAddressesError()));
        };
    } else {
        const getAddressesUrl = URLS.GET_ADDRESSES.replace('{storeId}', storeId).replace(
            '{noCache}',
            new Date().getTime().toString()
        );

        const config: AxiosRequestConfig = {
            method: 'GET',
            url: getAddressesUrl,
            headers: { 'Content-Type': 'application/json' },
        };

        return dispatch => {
            dispatch(actions.getAddressesPending());
            axios(config)
                .then(result => {
                    dispatch(actions.getAddressesSuccess(result?.data));
                })
                .catch(() => dispatch(actions.getAddressesError()));
        };
    }
};

export const addAddress = (
    isMockRequest: boolean,
    storeId: string,
    payload: AddressGroupPayload
) => {
    const addAddressUrl = URLS.ADD_ADDRESS.replace('{storeId}', storeId);
    const updateAddressCSTUrl = URLS.UPDATE_ADDRESS_CST.replace('{storeId}', storeId);

    const configs = Object.entries(payload).map(([_, address]) => {
        return {
            method: 'POST',
            url: addAddressUrl,
            headers: { 'Content-Type': 'application/json' },
            data: address,
        } as AxiosRequestConfig;
    }); // prepare all requests

    // prepare CST request
    const updateAddressCST: AxiosRequestConfig = {
        method: 'POST',
        url: updateAddressCSTUrl,
        headers: { 'Content-Type': 'application/json' },
        data: {
            action: 'add'
        },
    };

    return dispatch => {
        dispatch(actions.addAddressPending());
        axios
            .all(configs.map(c => axios(c))) // launch all requests in parallel and wait for responses to arrive
            .then(responses => {
                // successful responses contain the addressId, if all of them do we're fine
                responses.every(response => !!response.data?.addressId)
                    ? (dispatch(getAddresses(isMockRequest, storeId)),
                      dispatch(actions.addAddressSuccess()),
                      axios(updateAddressCST))
                    : dispatch(actions.addAddressError());
            })
            .catch(() => {
                dispatch(actions.addAddressError());
            });
    };
};

export const editAddress = (
    isMockRequest: boolean,
    storeId: string,
    payload: AddressGroupPayload
) => {
    const baseEditAddressUrl = URLS.EDIT_ADDRESS.replace('{storeId}', storeId);
    const updateAddressCSTUrl = URLS.UPDATE_ADDRESS_CST.replace('{storeId}', storeId);

    const configs = Object.entries(payload).map(([nickName, address]) => {
        const url = baseEditAddressUrl.replace('{nickName}', nickName);

        return {
            method: 'PUT',
            url: url,
            headers: { 'Content-Type': 'application/json' },
            data: address,
        } as AxiosRequestConfig;
    }); // prepare all requests

    // prepare CST request
    const updateAddressCST: AxiosRequestConfig = {
        method: 'POST',
        url: updateAddressCSTUrl,
        headers: { 'Content-Type': 'application/json' },
        data: {
            action: 'edit'
        },
    };

    return dispatch => {
        dispatch(actions.editAddressPending());
        axios
            .all(configs.map(c => axios(c))) // launch all requests in parallel and wait for responses to arrive
            .then(responses => {
                // successful responses contain the addressId, if all of them do we're fine
                responses.every(response => !!response.data?.addressId)
                    ? (dispatch(getAddresses(isMockRequest, storeId)),
                      dispatch(actions.editAddressSuccess()),
                      axios(updateAddressCST))
                    : dispatch(actions.editAddressError());
            })
            .catch(() => {
                dispatch(actions.editAddressError());
            });
    };
};

export const setDefaultAddress = (
    isMockRequest: boolean,
    storeId: string,
    ...nickNames: string[]
) => {
    const updateAddressCSTUrl = URLS.UPDATE_ADDRESS_CST.replace('{storeId}', storeId);

    const configs = nickNames
        .filter(nickName => !!nickName)
        .map(nickName => {
            const url = URLS.SET_DEFAULT_ADDRESS.replace('{storeId}', storeId).replace(
                '{nickName}',
                nickName
            );

            return {
                method: 'PUT',
                url: url,
                headers: { 'Content-Type': 'application/json' },
                data: {
                    contact: [
                        {
                            nickName: nickName,
                            primary: 'true',
                        },
                    ],
                },
            } as AxiosRequestConfig;
        });

    // prepare CST request
    const updateAddressCST: AxiosRequestConfig = {
        method: 'POST',
        url: updateAddressCSTUrl,
        headers: { 'Content-Type': 'application/json' },
        data: {
            action: 'default'
        },
    };

    return dispatch => {
        dispatch(actions.setDefaultAddressPending());
        axios
            .all(configs.map(c => axios(c)))
            .then(responses => {
                responses.every(response => !!response.data?.addressId)
                    ? (dispatch(getAddresses(isMockRequest, storeId)),
                      dispatch(actions.setDefaultAddressSuccess()),
                      axios(updateAddressCST))
                    : dispatch(actions.setDefaultAddressError());
            })
            .catch(() => {
                dispatch(actions.setDefaultAddressError());
            });
    };
};

export const deleteAddress = (isMockRequest: boolean, storeId: string, ...nickNames: string[]) => {
    const updateAddressCSTUrl = URLS.UPDATE_ADDRESS_CST.replace('{storeId}', storeId);

    const configs = nickNames
        .filter(nickName => !!nickName)
        .map(nickName => {
            const deleteAddressUrl = URLS.DELETE_ADDRESS.replace('{storeId}', storeId).replace(
                '{nickName}',
                nickName
            );

            return {
                method: 'DELETE',
                url: deleteAddressUrl,
                headers: { 'Content-Type': 'application/json' },
            } as AxiosRequestConfig;
        });

    // prepare CST request
    const updateAddressCST: AxiosRequestConfig = {
        method: 'POST',
        url: updateAddressCSTUrl,
        headers: { 'Content-Type': 'application/json' },
        data: {
            action: 'delete'
        },
    };

    return dispatch => {
        dispatch(actions.deleteAddressPending());
        axios
            .all(configs.map(c => axios(c)))
            .then(responses => {
                responses.every(response => !!response.data?.addressId)
                    ? (dispatch(getAddresses(isMockRequest, storeId)),
                      dispatch(actions.deleteAddressSuccess()),
                      axios(updateAddressCST))
                    : dispatch(actions.deleteAddressError());
            })
            .catch(() => {
                dispatch(actions.deleteAddressError());
            });
    };
};

//MY SAVED ITEMS

export const getMySavedItems = (payload: IMySavedItemsListPayload) => {
    const { isMockRequest, storeId, catalogId } = payload;
    if (isMockRequest) {
        const mySavedItems = '/data/mocks/my_saved_items_get_my_saved_items_mock.json';
        return dispatch => {
            dispatch(actions.getMySavedItemsPending());
            getMockedFile(mySavedItems)
                .then(result => {
                    if (
                        result?.data?.response?.success &&
                        result?.data?.response?.savedItems?.length
                    ) {
                        const mappedSavedItem = mapSavedItemsList(result.data.response);
                        dispatch(actions.getMySavedItemsSuccess(mappedSavedItem));
                    } else {
                        dispatch(actions.getMySavedItemsError());
                    }
                })
                .catch(() => dispatch(actions.getMySavedItemsError()));
        };
    } else {
        const mySavedItemsUrl = URLS.GET_MY_SAVED_ITEMS.replace('{storeId}', storeId.toString())
            .replace('{catalogId}', catalogId.toString())
            .replace('{noCache}', new Date().getTime().toString());

        const config: AxiosRequestConfig = {
            method: 'GET',
            url: mySavedItemsUrl,
            headers: { 'Content-Type': 'application/json' },
        };

        return dispatch => {
            dispatch(actions.getMySavedItemsPending());
            axios(config)
                .then(result => {
                    if (
                        result?.data?.response?.success &&
                        result?.data?.response?.savedItems?.length
                    ) {
                        const mappedSavedItem = mapSavedItemsList(result.data.response);
                        dispatch(actions.getMySavedItemsSuccess(mappedSavedItem));
                    } else {
                        dispatch(actions.getMySavedItemsError());
                    }
                })
                .catch(() => dispatch(actions.getMySavedItemsError()));
        };
    }
};
